/* eslint-disable no-underscore-dangle */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Badge, Button, Form, Modal,
} from 'react-bootstrap';
import dayjs from 'dayjs';
import Error from '../../../Components/Error';
import PaymentsService from '../../../Services/PaymentsService';
import { useAccountsList } from '../../../Hooks/useAccountsCache';
import { useCounterpartyNames } from '../../../Hooks/useCounterpartyNames';
import { formatDate } from '../../../Utils/timezone';
import useDocumentIssuer from '../../../Components/Billingo/useDocumentIssuer';
import paymentBookingAllocations from './paymentBookingAllocations';
import PayerBillingDetails from './PayerBillingDetails';
import { formatPaymentAmount, PaymentStatusBadge } from './paymentDisplay';
import { isRefundable, refundedOriginalIds } from '../../../Utils/paymentStatus';
import occurredAtIso from '../../../Utils/occurredAt';

// Convert an ISO string to the YYYY-MM-DD format the native
// <input type="date"> expects. Returns '' for null/undefined/invalid
// values so the controlled input never gets a React warning.
function toDateInputValue(iso) {
  if (!iso) return '';
  const d = dayjs(iso);
  if (!d.isValid()) return '';
  return d.format('YYYY-MM-DD');
}

// Build the patch by diffing the form values against the original
// payment. We only send fields that actually changed. Empty string ->
// null (explicit "clear" semantic) when the field originally had a
// non-empty value. Empty -> empty is a no-op.
function buildPatch(original, form) {
  const patch = {};
  // notes
  const origNotes = original?.notes ?? '';
  const newNotes = form.notes ?? '';
  if (origNotes !== newNotes) {
    patch.notes = newNotes === '' ? null : newNotes;
  }
  // bank_reference lives under external_refs in the Payment shape
  const origBankRef = original?.external_refs?.bank_reference ?? '';
  const newBankRef = form.bankReference ?? '';
  if (origBankRef !== newBankRef) {
    patch.bank_reference = newBankRef === '' ? null : newBankRef;
  }
  // occurred_at — compare in YYYY-MM-DD (the date input's resolution)
  // because the original ISO carries a time-of-day the input doesn't
  // expose. Only send when the date portion actually changed.
  const origDate = toDateInputValue(original?.occurred_at);
  const newDate = form.occurredOn ?? '';
  if (origDate !== newDate && newDate !== '') {
    // Send a midnight-UTC ISO so the value round-trips cleanly. The
    // backend stores ISO strings; sending a naked YYYY-MM-DD would
    // depend on backend coercion that may vary.
    patch.occurred_at = occurredAtIso(newDate);
  }
  return patch;
}

function PaymentDetailsModal({
  show,
  onHide,
  payment,
  relatedPayments = [],
  onUpdated,
  onRefund,
  onLinkToBooking,
  onEditAllocation,
}) {
  const [editMode, setEditMode] = useState(false);
  const [form, setForm] = useState({ notes: '', bankReference: '', occurredOn: '' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  // Billingo: create an advance/final invoice from this payment and assign
  // the payment to the related order.
  const documentIssuer = useDocumentIssuer();

  // Resolve `payment.account_id` → Account.name via the shared cache.
  // v1 deliberately does NOT allow editing the account here — the
  // account is set at create time and changed via the
  // refund-and-re-record flow.
  const { byId: accountsById } = useAccountsList();
  const { partnersById, operatorsById } = useCounterpartyNames();

  // Seed/reset the form whenever the modal is (re)opened for a
  // different payment. Without this, switching from one row to
  // another would keep stale values.
  useEffect(() => {
    if (show && payment) {
      setForm({
        notes: payment.notes ?? '',
        bankReference: payment.external_refs?.bank_reference ?? '',
        occurredOn: toDateInputValue(payment.occurred_at),
      });
      setEditMode(false);
      setError('');
      setSubmitting(false);
    }
  }, [show, payment?._id]);

  const patch = useMemo(
    () => (payment ? buildPatch(payment, form) : {}),
    [payment, form],
  );
  const patchIsEmpty = Object.keys(patch).length === 0;

  // Inbound, completed and not already the parent of an offset row in the
  // loaded set — the rule every payment table uses (Utils/paymentStatus).
  const refundedIds = useMemo(() => refundedOriginalIds(relatedPayments), [relatedPayments]);

  const refundable = !!payment && isRefundable(payment, refundedIds);

  // Every booking the payment is attributed to — the counterparty only
  // names the first one of a split.
  const allocations = useMemo(() => paymentBookingAllocations(payment), [payment]);

  const canLinkToBooking = !!payment && payment.counterparty?.kind === 'system';
  // Complement of canLinkToBooking, and the same gating the table uses:
  // a payment already attributed to a booking can have its order-line
  // split re-cut instead of being refunded and re-recorded.
  const canEditAllocation = refundable && payment.counterparty?.kind === 'booking';
  // Incoming payments already tied to a booking can be invoiced (Előlegszámla /
  // Számla) and assigned to that order.
  const canInvoice = !!payment
    && payment.direction === 'in'
    && payment.counterparty?.kind === 'booking'
    && !!payment.counterparty?.id;

  const handleInvoice = () => documentIssuer.issue({
    group_key: payment._id,
    booking_ids: [payment.counterparty.id],
    payment,
  });

  // Refund chain: this row's parent (if any) plus any children
  // (relatedPayments whose parent_payment_id matches this row). Used
  // to render the small read-only history block at the bottom of the
  // summary card.
  const refundChain = useMemo(() => {
    if (!payment) return [];
    const out = [];
    if (payment.parent_payment_id) {
      const parent = relatedPayments.find((p) => p._id === payment.parent_payment_id);
      if (parent) out.push({ role: 'parent', payment: parent });
    }
    relatedPayments
      .filter((p) => p.parent_payment_id === payment._id)
      .forEach((child) => out.push({ role: 'child', payment: child }));
    return out;
  }, [payment, relatedPayments]);

  const handleSave = async () => {
    if (!payment || patchIsEmpty) return;
    setSubmitting(true);
    setError('');
    try {
      const updated = await PaymentsService.updatePayment(payment._id, patch);
      if (onUpdated) onUpdated(updated);
      setEditMode(false);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to update payment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelEdit = () => {
    if (submitting) return;
    if (payment) {
      setForm({
        notes: payment.notes ?? '',
        bankReference: payment.external_refs?.bank_reference ?? '',
        occurredOn: toDateInputValue(payment.occurred_at),
      });
    }
    setEditMode(false);
    setError('');
  };

  const handleClose = () => {
    if (submitting) return;
    if (onHide) onHide();
  };

  if (!payment) return null;

  return (
    <Modal show={show} onHide={handleClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="fa fa-receipt me-2" />
          Payment details
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Error error={error} setError={setError} />
        <Error error={documentIssuer.error} setError={documentIssuer.setError} />

        {/* Read-only summary card. Always visible — the edit form
            below it operates on a subset of these fields. */}
        <div className="mb-3 p-3 bg-light rounded">
          <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
            <div>
              <div className="text-muted small">Counterparty</div>
              <div className="fw-bold">
                {payment.counterparty?.kind || '—'}
                {' '}
                <i className="fa fa-arrow-right text-muted mx-1" />
                {' '}
                {/* Prefer the enriched booking_reference for booking
                    counterparties (the underlying ObjectId is still
                    surfaced via title= for admin debugging). Falls
                    back to the raw id for non-booking kinds or rows
                    whose booking lookup failed. */}
                {(() => {
                  const cp = payment.counterparty;
                  if (!cp) return <code>—</code>;
                  if (cp.kind === 'booking' && cp.booking_reference) {
                    return (
                      <>
                        <code title={cp.id}>{cp.booking_reference}</code>
                        {cp.booking_full_name && (
                          <span className="text-muted ms-2 small">
                            {cp.booking_full_name}
                          </span>
                        )}
                      </>
                    );
                  }
                  if (cp.kind === 'partner') {
                    const partner = partnersById.get(cp.id);
                    const name = partner?.name || partner?.Name;
                    return name
                      ? <span title={cp.id}>{name}</span>
                      : <code title={cp.id}>{cp.id || '—'}</code>;
                  }
                  if (cp.kind === 'operator') {
                    const op = operatorsById.get(cp.id);
                    const name = op?.Name || op?.name;
                    return name
                      ? <span title={cp.id}>{name}</span>
                      : <code title={cp.id}>{cp.id || '—'}</code>;
                  }
                  if (cp.kind === 'system') {
                    return <span className="text-muted">{cp.id}</span>;
                  }
                  return <code title={cp.id}>{cp.id || '—'}</code>;
                })()}
              </div>
            </div>
            <div className="text-end">
              <div className="text-muted small">Status</div>
              <PaymentStatusBadge status={payment.status} />
            </div>
          </div>

          {/* Who paid, from the Stripe billing details on the payment
              link. Sits directly under the counterparty because
              "attributed to X / paid by Y" is the comparison being made.
              Renders nothing for outbound payments. */}
          <PayerBillingDetails payment={payment} className="mt-2" />

          <hr className="my-2" />

          <div className="row g-2 small">
            <div className="col-sm-6">
              <div className="text-muted">Direction</div>
              <Badge bg={payment.direction === 'in' ? 'success' : 'secondary'}>
                {payment.direction || '—'}
              </Badge>
            </div>
            <div className="col-sm-6">
              <div className="text-muted">Account</div>
              <div>
                {(() => {
                  const acc = payment.account_id ? accountsById.get(payment.account_id) : null;
                  if (acc) return acc.name;
                  return <span className="text-muted">—</span>;
                })()}
              </div>
            </div>
            <div className="col-sm-4">
              <div className="text-muted">Gross</div>
              <div className="fw-bold">
                {formatPaymentAmount(payment.gross_amount, payment.currency)}
              </div>
            </div>
            <div className="col-sm-4">
              <div className="text-muted">Fee</div>
              <div>{formatPaymentAmount(payment.fee, payment.currency)}</div>
            </div>
            <div className="col-sm-4">
              <div className="text-muted">Net</div>
              <div className="fw-bold">
                {formatPaymentAmount(payment.net_amount, payment.currency)}
              </div>
            </div>
            <div className="col-sm-6">
              <div className="text-muted">Occurred</div>
              <div>{payment.occurred_at ? formatDate(payment.occurred_at) : '—'}</div>
            </div>
            <div className="col-sm-6">
              <div className="text-muted">Currency</div>
              <div>{payment.currency || '—'}</div>
            </div>
          </div>

          {allocations.length > 1 && (
            <>
              <hr className="my-2" />
              <div className="small">
                <div className="text-muted">{`Allocated to ${allocations.length} bookings`}</div>
                <ul className="list-unstyled mb-0">
                  {allocations.map((a) => (
                    <li key={a.booking_id} className="d-flex justify-content-between gap-2">
                      <code title={a.booking_id}>{a.booking_reference || a.booking_id}</code>
                      <span>{formatPaymentAmount(a.gross_amount, payment.currency)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </>
          )}

          {(payment.external_refs?.stripe_charge_id
            || payment.external_refs?.stripe_payout_id
            || payment.external_refs?.bank_reference
            || payment.external_refs?.barion_payment_id) && (
            <>
              <hr className="my-2" />
              <div className="row g-2 small">
                {payment.external_refs?.stripe_charge_id && (
                  <div className="col-sm-6">
                    <div className="text-muted">Stripe charge</div>
                    <code>{payment.external_refs.stripe_charge_id}</code>
                  </div>
                )}
                {payment.external_refs?.stripe_payout_id && (
                  <div className="col-sm-6">
                    <div className="text-muted">Stripe payout</div>
                    <code>{payment.external_refs.stripe_payout_id}</code>
                  </div>
                )}
                {payment.external_refs?.bank_reference && (
                  <div className="col-sm-6">
                    <div className="text-muted">Bank reference</div>
                    <code>{payment.external_refs.bank_reference}</code>
                  </div>
                )}
                {payment.external_refs?.barion_payment_id && (
                  <div className="col-sm-6">
                    <div className="text-muted">Barion payment</div>
                    <code>{payment.external_refs.barion_payment_id}</code>
                  </div>
                )}
              </div>
            </>
          )}

          {payment.notes && !editMode && (
            <>
              <hr className="my-2" />
              <div className="small">
                <div className="text-muted">Notes</div>
                <div>{payment.notes}</div>
              </div>
            </>
          )}
        </div>

        {/* Refund chain block — only when there's something to show. */}
        {refundChain.length > 0 && (
          <div className="mb-3">
            <div className="text-muted small mb-1">Refund history</div>
            <ul className="list-unstyled small mb-0">
              {refundChain.map(({ role, payment: rp }) => (
                <li key={rp._id} className="mb-1">
                  <Badge bg="light" text="dark" className="me-2">
                    {role === 'parent' ? '↩ Original' : '↪ Refund'}
                  </Badge>
                  <code className="me-2">{rp._id}</code>
                  {formatPaymentAmount(rp.gross_amount, rp.currency)}
                  {' · '}
                  {rp.occurred_at ? formatDate(rp.occurred_at) : '—'}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Edit panel */}
        {!editMode ? (
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => setEditMode(true)}
          >
            <i className="fa fa-pen me-1" />
            Edit
          </Button>
        ) : (
          <Form onSubmit={(e) => { e.preventDefault(); handleSave(); }}>
            <Form.Group className="mb-3" controlId="pdm_notes">
              <Form.Label>Notes</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={form.notes}
                onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                disabled={submitting}
              />
            </Form.Group>
            <Form.Group className="mb-3" controlId="pdm_bank_ref">
              <Form.Label>Bank reference</Form.Label>
              <Form.Control
                type="text"
                value={form.bankReference}
                onChange={(e) => setForm((f) => ({ ...f, bankReference: e.target.value }))}
                disabled={submitting}
              />
            </Form.Group>
            <Form.Group className="mb-3" controlId="pdm_occurred_on">
              <Form.Label>Occurred on</Form.Label>
              <Form.Control
                type="date"
                value={form.occurredOn}
                onChange={(e) => setForm((f) => ({ ...f, occurredOn: e.target.value }))}
                disabled={submitting}
              />
              <Form.Text className="text-muted">
                Date the admin recorded receipt. Saved as midnight UTC.
              </Form.Text>
            </Form.Group>

            <div className="d-flex gap-2">
              <Button
                variant="primary"
                type="submit"
                disabled={submitting || patchIsEmpty}
                title={patchIsEmpty ? 'No changes to save' : ''}
              >
                {submitting ? 'Saving…' : 'Save changes'}
              </Button>
              <Button
                variant="outline-secondary"
                onClick={handleCancelEdit}
                disabled={submitting}
                type="button"
              >
                Cancel edit
              </Button>
            </div>
          </Form>
        )}
      </Modal.Body>
      <Modal.Footer className="d-flex flex-wrap gap-2 justify-content-between">
        <div className="d-flex flex-wrap gap-2">
          {refundable && onRefund && (
            <Button
              variant="outline-danger"
              size="sm"
              onClick={() => onRefund(payment)}
              disabled={submitting}
            >
              <i className="fa fa-undo me-1" />
              Refund this payment
            </Button>
          )}
          {canLinkToBooking && onLinkToBooking && (
            <Button
              variant="outline-primary"
              size="sm"
              onClick={() => onLinkToBooking(payment)}
              disabled={submitting}
            >
              <i className="fa fa-link me-1" />
              Link to booking
            </Button>
          )}
          {canEditAllocation && onEditAllocation && (
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => onEditAllocation(payment)}
              disabled={submitting}
            >
              <i className="fa fa-pencil me-1" />
              Edit allocation
            </Button>
          )}
          {canInvoice && (
            <Button
              variant="outline-primary"
              size="sm"
              onClick={handleInvoice}
              disabled={submitting || !!documentIssuer.preparing}
            >
              <i className="fa fa-file-invoice me-1" />
              {documentIssuer.preparing ? 'Loading…' : 'Invoice this payment'}
            </Button>
          )}
        </div>
        <Button variant="secondary" onClick={handleClose} disabled={submitting}>
          Close
        </Button>
      </Modal.Footer>

      {documentIssuer.modal}
    </Modal>
  );
}

export default PaymentDetailsModal;
